import React, { useContext, useState, useEffect } from 'react'

import { RouteComponentProps, withRouter } from 'react-router-dom'
import { InputOnChangeData } from 'semantic-ui-react'

// Specs
import Data from '../../api/Data'
import { IUser, ITeam, IBrand, ITheme, IProject } from '../../api/Data'
import { teamAvatarSize, teamLogoSize, teamWatermarkSize } from '../../config/constants'
import { DEFAULT_PROJECT_TYPE, ProjectType } from '../../config/projectTypes'
import { UserContext } from '../../contexts/UserContext'
import { extractDomain, getAvailableFonts, wrapBrand } from '../../utils/sharedFunctions'
import { createWhiteWatermarkBlob } from '../../utils/watermark'
import { buildProjectCreationPayload } from '../../utils/projectCreation'
import {
  createAttributionTheme,
  DEFAULT_PROJECT_ATTRIBUTION_SETTINGS,
  findProjectThemeSeed,
  ProjectAttributionSettings,
  suggestProjectName,
} from '../../utils/projectSetup'

import ImageUploader from '../../components/modals/ImageUploader'

import BrandingModal from './BrandingModal'
import ProjectLinkModal from './ProjectLinkModal'
import ProjectNameModal from './ProjectNameModal'
import ProjectThemeModal from './ProjectThemeModal'
import StyleModal from './StyleModal'
import SubscriptionConfirmationModal from './SubscriptionConfirmationModal'
import WelcomeModal from './WelcomeModal'

var tinycolor = require('tinycolor2')

const OnboardingPage = ({ history }: IProps) => {
  const { userProfile, dispatchUserContext } = useContext(UserContext)

  // States
  const [step, setStep] = useState(userProfile.isCustomer ? 0 : 1)
  const [user] = useState<IUser>(userProfile)
  const [team, setTeam] = useState<ITeam | null>(null)
  const [brand, setBrand] = useState<IBrand | null>(null)
  const [themes, setThemes] = useState<ITheme[] | null>(null)
  const [projectName, setProjectName] = useState<string>('')
  const [projectNameWasEdited, setProjectNameWasEdited] = useState(false)
  const [projectType, setProjectType] = useState<ProjectType>(DEFAULT_PROJECT_TYPE)
  const [attributionSettings, setAttributionSettings] = useState<ProjectAttributionSettings>(
    DEFAULT_PROJECT_ATTRIBUTION_SETTINGS
  )
  const [firstProject, setFirstProject] = useState<IProject | null>(null)
  const [loading, setIsLoading] = useState(false)
  const [showIconUploadModal, setShowIconUploadModal] = useState(false)
  const [showFullLogoUploadModal, setShowFullLogoUploadModal] = useState(false)
  const [showWatermarkUploadModal, setShowWatermarkUploadModal] = useState(false)

  useEffect(() => {
    const fetchTeam = async () => {
      const { data } = await Data.getMyTeam()

      setTeam(data)
      setProjectName((currentName) => currentName || suggestProjectName(data.teamName, DEFAULT_PROJECT_TYPE))
    }
    fetchTeam()
  }, [])

  useEffect(() => {
    const fetchThemes = async () => {
      const { data } = await Data.getThemes()

      setThemes(data)
    }
    fetchThemes()
  }, [])

  // Change team logo
  const handleSaveTeamAvatar = async (imageBlob: Blob) => {
    const formData = new FormData()

    formData.append('imageBlob', imageBlob)

    setIsLoading(true)

    const { error, data } = await Data.updateTeamAvatar(formData)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred')
    }

    const { logoAvatarUrl } = data

    // Necessary to display the new team logo
    setTeam((preState: any) => ({ ...preState, logoAvatarUrl: logoAvatarUrl }))

    dispatchUserContext({ type: 'update user profile', payload: { logoAvatarUrl } })

    setIsLoading(false)

    setShowIconUploadModal(false)

    return true
  }

  // Change full team logo
  const handleSaveFullTeamLogo = async (imageBlob: Blob) => {
    const formData = new FormData()

    formData.append('imageBlob', imageBlob)

    setIsLoading(true)

    const { error, data } = await Data.updateTeamLogo(formData)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred')
    }

    const { logoUrl } = data

    setTeam((preState: any) => ({ ...preState, logoUrl }))
    dispatchUserContext({ type: 'update user profile', payload: { logoUrl } })

    setIsLoading(false)
    setShowFullLogoUploadModal(false)

    return true
  }

  // Change team watermark
  const handleSaveTeamWatermark = async (imageBlob: Blob) => {
    const formData = new FormData()

    formData.append('imageBlob', imageBlob)

    setIsLoading(true)

    const { error, data } = await Data.updateTeamWatermark(formData)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred')
    }

    const { logoWatermarkUrl } = data

    setTeam((preState: any) => ({ ...preState, logoWatermarkUrl: logoWatermarkUrl }))

    dispatchUserContext({ type: 'update user profile', payload: { logoWatermarkUrl } })

    setIsLoading(false)

    setShowWatermarkUploadModal(false)

    return true
  }

  const handleChangeTeamName = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const { value } = data

    setTeam((preState: any) => ({ ...preState, teamName: value }))
    if (!projectNameWasEdited) setProjectName(suggestProjectName(value, projectType))
  }

  const handleChangeOrganisationWebsite = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const { value } = data

    setTeam((preState: any) => ({ ...preState, organisationWebsite: value.trim() }))
  }

  const handleChangeLegalEntity = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const { value } = data

    setTeam((preState: any) => ({ ...preState, contentOwner: value }))
  }

  const handleChangeFont = (newFont: { family: string }) => {
    const newFontFamily = newFont.family

    setTeam((preState: any) => ({ ...preState, primaryFont: newFontFamily }))
  }

  const handleChangePrimaryColor = (newColor: { hex: string }) => {
    const hexColor = newColor.hex

    setTeam((preState: any) => ({
      ...preState,
      ...{
        primaryColor: hexColor,
        buttonTextColor: tinycolor(hexColor).getBrightness() >= 200 ? '#000000' : '#FFFFFF',
      },
    }))
  }

  const handleChangeSecondaryColor = (newColor: { hex: string }) => {
    const hexColor = newColor.hex

    setTeam((preState: any) => ({ ...preState, secondaryColor: hexColor }))
  }

  const handleCancelAvatarUpload = () => {
    setShowIconUploadModal(false)
  }

  const handleCancelFullLogoUpload = () => {
    setShowFullLogoUploadModal(false)
  }

  const handleCancelWatermarkUpload = () => {
    setShowWatermarkUploadModal(false)
  }

  const handleChangeProjectName = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const { value } = data

    setProjectNameWasEdited(true)
    setProjectName(value)
  }

  const handleChangeProjectType = (newProjectType: ProjectType) => {
    setProjectType(newProjectType)

    if (!projectNameWasEdited) {
      setProjectName(suggestProjectName(team?.teamName || userProfile.teamName, newProjectType))
    }
  }

  const stepBack = () => {
    setStep((prevState) => prevState - 1)
  }

  const stepForward = () => {
    setStep((prevState) => prevState + 1)
  }

  const handleSaveBranding = async () => {
    setIsLoading(true)

    let newTeamData: any = {}
    let brandData: any = null

    try {
      // Brandfetch integration
      if (!!team!.organisationWebsite) {
        const domain = extractDomain(team!.organisationWebsite)

        if (!!domain) {
          const { data, error } = await Data.getBrand(domain)

          if (!error) {
            const availableFonts = await getAvailableFonts()

            brandData = wrapBrand(data)

            newTeamData = {
              ...newTeamData,
              ...{
                organisationWebsite: brandData.organisationWebsite || team!.organisationWebsite,
                organisationDescription: brandData.organisationDescription || team!.organisationDescription,
                primaryFont: !!availableFonts.find((f: any) => f.family === brandData.primaryFont)
                  ? brandData.primaryFont
                  : 'Roboto',
                primaryColor: brandData.primaryColor || team!.primaryColor,
                secondaryColor: brandData.secondaryColor || team!.secondaryColor,
                buttonTextColor:
                  tinycolor(brandData.primaryColor || team!.primaryColor).getBrightness() >= 200
                    ? '#000000'
                    : '#FFFFFF',
              },
            }

            const iconSourceUrl = brandData.logoAvatarUrl || brandData.logoUrl
            const fullLogoSourceUrl = brandData.logoUrl || brandData.logoAvatarUrl
            const watermarkSourceUrl = brandData.logoWatermarkSourceUrl || brandData.logoUrl || brandData.logoAvatarUrl

            if (iconSourceUrl) {
              const blob = await fetch(iconSourceUrl).then((r) => r.blob())
              const formData = new FormData()
              formData.append('imageBlob', blob)
              const result = await Data.updateTeamAvatar(formData)

              if (result.error) throw result.error
              newTeamData.logoAvatarUrl = result.data.logoAvatarUrl
            }

            if (fullLogoSourceUrl) {
              const blob = await fetch(fullLogoSourceUrl).then((r) => r.blob())
              const formData = new FormData()
              formData.append('imageBlob', blob)
              const result = await Data.updateTeamLogo(formData)

              if (result.error) throw result.error
              newTeamData.logoUrl = result.data.logoUrl
            }

            if (watermarkSourceUrl) {
              const sourceBlob = await fetch(watermarkSourceUrl).then((r) => r.blob())
              const blob = await createWhiteWatermarkBlob(sourceBlob)
              const formData = new FormData()
              formData.append('imageBlob', blob)
              const result = await Data.updateTeamWatermark(formData)

              if (result.error) throw result.error
              newTeamData.logoWatermarkUrl = result.data.logoWatermarkUrl
            }
          }
        }
      }
    } catch (error) {
      setIsLoading(false)
      return alert('The new branding could not be saved. Please try again or upload the assets manually.')
    }

    const nextTeam = { ...team, ...newTeamData } as ITeam

    setTeam(nextTeam)
    setBrand(brandData)

    const { data, error } = await Data.updateTeam(nextTeam)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred during the update')
    }

    const {
      teamName,
      organisationWebsite,
      organisationDescription,
      primaryFont,
      primaryColor,
      secondaryColor,
      buttonTextColor,
      logoAvatarUrl,
      logoUrl,
      logoWatermarkUrl,
    } = data

    dispatchUserContext({
      type: 'update user profile',
      payload: {
        teamName,
        organisationWebsite,
        organisationDescription,
        primaryFont,
        primaryColor,
        secondaryColor,
        buttonTextColor,
        logoAvatarUrl,
        logoUrl,
        logoWatermarkUrl,
      },
    })

    setIsLoading(false)

    stepForward()
  }

  const handleSaveStyle = async () => {
    setIsLoading(true)

    const { data, error } = await Data.updateTeam(team)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred during the update')
    }

    const { primaryFont, primaryColor, secondaryColor, buttonTextColor } = data

    dispatchUserContext({
      type: 'update user profile',
      payload: { primaryFont, primaryColor, secondaryColor, buttonTextColor },
    })

    setIsLoading(false)

    stepForward()
  }

  const handleSaveProject = async () => {
    setIsLoading(true)

    const projectTheme = findProjectThemeSeed(themes!)

    if (!projectTheme) {
      setIsLoading(false)
      return alert('A project layout seed is not available. Please contact support.')
    }

    const { data, error } = await Data.createProject(
      buildProjectCreationPayload({
        projectName,
        projectType,
        teamName: team!.teamName,
        theme: projectTheme,
      })
    )

    if (error) {
      setIsLoading(false)
      return alert('An error occurred during the create')
    }

    const { data: projectDetail, error: projectDetailError } = await Data.getProject(data.project.slug)

    if (!projectDetailError && projectDetail?.theme) {
      const { error: themeUpdateError } = await Data.updateTheme(
        createAttributionTheme(projectDetail.theme, attributionSettings, team!)
      )

      if (themeUpdateError) {
        alert(
          'The project was created, but its attribution layout could not be applied. You can edit it in Template Builder.'
        )
      }
    }

    setFirstProject(data.project)

    setIsLoading(false)

    stepForward()
  }

  const handleEndOnboarding = async () => {
    history.push('/projects')

    window.location.reload()
  }

  const handleConfigureProject = () => {
    if (!firstProject) return

    history.push(`/projects/${firstProject.slug}/configure?tab=overview`)
    window.location.reload()
  }

  if (!team || !themes) return null

  return (
    <>
      {step === 0 && <SubscriptionConfirmationModal user={user} team={team} stepForward={stepForward} />}
      {step === 1 && <WelcomeModal stepForward={stepForward} />}
      {step === 2 && (
        <BrandingModal
          loading={loading}
          user={user}
          team={team}
          handleChangeTeamName={handleChangeTeamName}
          handleChangeLegalEntity={handleChangeLegalEntity}
          handleChangeOrganisationWebsite={handleChangeOrganisationWebsite}
          stepBack={stepBack}
          handleSaveBranding={handleSaveBranding}
        />
      )}
      {step === 3 && (
        <StyleModal
          loading={loading}
          team={team}
          brand={brand}
          setShowIconUploadModal={setShowIconUploadModal}
          setShowFullLogoUploadModal={setShowFullLogoUploadModal}
          setShowWatermarkUploadModal={setShowWatermarkUploadModal}
          handleChangeFont={handleChangeFont}
          handleChangePrimaryColor={handleChangePrimaryColor}
          handleChangeSecondaryColor={handleChangeSecondaryColor}
          stepBack={stepBack}
          handleSaveStyle={handleSaveStyle}
          handleEndOnboarding={handleEndOnboarding}
        />
      )}
      {step === 4 && (
        <ProjectNameModal
          loading={loading}
          projectName={projectName}
          projectType={projectType}
          handleChangeProjectName={handleChangeProjectName}
          handleChangeProjectType={handleChangeProjectType}
          stepBack={stepBack}
          stepForward={stepForward}
          handleEndOnboarding={handleEndOnboarding}
        />
      )}
      {step === 5 && (
        <ProjectThemeModal
          loading={loading}
          watermarkUrl={team.logoWatermarkUrl}
          brandColor={team.primaryColor}
          attributionSettings={attributionSettings}
          handleChangeAttributionSettings={setAttributionSettings}
          stepBack={stepBack}
          handleSaveProject={handleSaveProject}
          handleEndOnboarding={handleEndOnboarding}
        />
      )}
      {step === 6 && firstProject !== null && (
        <ProjectLinkModal team={team} project={firstProject} handleConfigureProject={handleConfigureProject} />
      )}
      {showIconUploadModal && (
        <ImageUploader
          inputImageType={teamAvatarSize.inputImageType}
          outputImageType={teamAvatarSize.outputImageType}
          imageIsAvatar={teamAvatarSize.imageIsAvatar}
          imageSize={{ width: teamAvatarSize.width, height: teamAvatarSize.height }}
          loading={loading}
          handleSave={handleSaveTeamAvatar}
          handleCancel={handleCancelAvatarUpload}
        />
      )}
      {showFullLogoUploadModal && (
        <ImageUploader
          inputImageType={teamLogoSize.inputImageType}
          outputImageType={teamLogoSize.outputImageType}
          imageIsAvatar={teamLogoSize.imageIsAvatar}
          imageSize={{ width: teamLogoSize.width, height: teamLogoSize.height }}
          loading={loading}
          handleSave={handleSaveFullTeamLogo}
          handleCancel={handleCancelFullLogoUpload}
        />
      )}
      {showWatermarkUploadModal && (
        <ImageUploader
          inputImageType={teamWatermarkSize.inputImageType}
          outputImageType={teamWatermarkSize.outputImageType}
          imageIsAvatar={teamWatermarkSize.imageIsAvatar}
          imageSize={{ width: teamWatermarkSize.width, height: teamWatermarkSize.height }}
          loading={loading}
          transformImage={createWhiteWatermarkBlob}
          handleSave={handleSaveTeamWatermark}
          handleCancel={handleCancelWatermarkUpload}
        />
      )}
    </>
  )
}

interface IProps extends RouteComponentProps<any> {}

export default withRouter(OnboardingPage)
